import { HttpErrorResponse } from '@angular/common/http';
import { Component, effect, inject, OnInit } from '@angular/core';
import { Dialog } from '@angular/cdk/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs';
import { ComponentCredentialDto } from 'src/app/models/component-credentials.model';
import {
  LicensedProductComponentDto,
  ScopeDto,
} from 'src/app/models/license.model';
import { LicenseBatchStore } from 'src/app/services/license-batch.store';
import { ComponentCredentialsService } from 'src/app/services/component-credentials.service';
import { LicensedProductService } from 'src/app/services/licensed-product.service';
import { InformationModalComponent } from '../modals/information-modal/information-modal.component';

@Component({
  selector: 'app-component-credentials-form',
  templateUrl: './component-credentials-form.component.html',
  styleUrls: ['./component-credentials-form.component.scss'],
  standalone: false,
})
export class ComponentCredentialsFormComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dialog = inject(Dialog);
  private readonly componentCredentialsService = inject(
    ComponentCredentialsService,
  );
  private readonly licenseBatchStore = inject(LicenseBatchStore);
  private readonly licensedProductService = inject(LicensedProductService);
  componentOptions: LicensedProductComponentDto[] = [];
  selectedComponent: LicensedProductComponentDto | null = null;
  scopes: ScopeDto[] = [];
  selectedScope: ScopeDto | null = null;

  form: FormGroup = this.formBuilder.nonNullable.group({
    componentId: this.formBuilder.control('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    description: this.formBuilder.control('', {
      nonNullable: true,
    }),
    scopeId: this.formBuilder.control('', {
      nonNullable: true,
      validators: [Validators.maxLength(120)],
    }),
    clientId: this.formBuilder.control('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(180)],
    }),
    clientSecret: this.formBuilder.control('', {
      nonNullable: true,
      validators: [Validators.maxLength(10000)],
    }),
  });

  editingCredentialId: string | null = null;
  loading = false;
  saving = false;
  showResponseBanner = false;
  successfulSave = false;
  successMessage = 'Component credential saved.';
  errorMessage = 'Failed to save component credential.';
  private validationWarningDialogOpen = false;
  private shouldNavigateAfterValidationDialogClose = false;

  get isEditMode(): boolean {
    return this.editingCredentialId !== null;
  }

  get scopeIdControl() {
    return this.form.controls['scopeId'];
  }

  get componentIdControl() {
    return this.form.controls['componentId'];
  }

  get componentPreSelectedValue(): LicensedProductComponentDto | string | null {
    if (this.selectedComponent) {
      return this.selectedComponent;
    }

    const componentId = this.componentIdControl.value.trim();
    return componentId || null;
  }

  get scopePreSelectedValue(): ScopeDto | string | null {
    if (this.selectedScope) {
      return this.selectedScope;
    }

    const scopeId = this.scopeIdControl.value.trim();
    return scopeId || null;
  }

  constructor() {
    effect(() => {
      const scopedOptions = this.licenseBatchStore.scopes();
      this.scopes = [...scopedOptions];

      if (
        this.selectedScope &&
        !this.scopes.some((scope) => scope.id === this.selectedScope?.id)
      ) {
        this.scopes = [...this.scopes, this.selectedScope];
      }

      const selectedScopeId = this.scopeIdControl.value.trim();
      if (!selectedScopeId) {
        return;
      }

      const matchedScope = this.findScopeById(selectedScopeId);
      if (matchedScope) {
        this.selectedScope = matchedScope;
      }
    });
  }

  ngOnInit(): void {
    this.loadScopes();
    this.loadComponents();

    const credentialId = this.route.snapshot.paramMap.get('id');
    if (!credentialId) {
      return;
    }

    this.editingCredentialId = credentialId;
    this.loadCredential(credentialId);
  }

  getComponentLabel(componentId: string): string {
    const rawLabel =
      this.componentOptions.find((component) => component.id === componentId)
        ?.componentName ?? componentId;
    return this.humanizeLabel(rawLabel);
  }

  onComponentSelected(selected: LicensedProductComponentDto | string): void {
    let component: LicensedProductComponentDto | undefined;
    if (typeof selected === 'string') {
      component = this.findComponentByNameOrId(selected);
    } else {
      component = selected;
    }

    this.selectedComponent = component ?? null;
    this.componentIdControl.setValue(component?.id?.trim() ?? '');
    this.componentIdControl.markAsTouched();
  }

  componentFormatter = (
    item: LicensedProductComponentDto | null,
    value: string,
  ): string => {
    if (!item) {
      return this.humanizeLabel(value);
    }

    const componentName = this.humanizeLabel(
      item.componentName?.trim() || item.id || 'Unnamed Component',
    );
    const componentId = this.humanizeLabel(item.id?.trim() || '');

    if (
      !componentId ||
      componentName.toLowerCase() === componentId.toLowerCase()
    ) {
      return componentName;
    }

    return `${componentName} (${componentId})`;
  };

  formatScopeLabel(scope: ScopeDto): string {
    const scopeName = this.humanizeLabel(scope.name?.trim() || 'Unnamed Scope');
    const scopeId = this.humanizeLabel(scope.id?.trim() || '');

    if (!scopeId || scopeName.toLowerCase() === scopeId.toLowerCase()) {
      return scopeName;
    }
    return `${scopeName} (${scopeId})`;
  }

  onScopeSelected(selected: ScopeDto | string): void {
    let scope: ScopeDto | undefined;
    if (typeof selected === 'string') {
      scope = this.findScopeByNameOrId(selected);
    } else {
      scope = selected;
    }

    this.selectedScope = scope ?? null;
    this.scopeIdControl.setValue(scope?.id?.trim() ?? '');
    this.scopeIdControl.markAsTouched();
  }

  scopeFormatter = (item: ScopeDto | null, value: string): string => {
    if (!item) {
      return value;
    }
    return this.formatScopeLabel(item);
  };

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const componentId = this.form.controls['componentId'].value.trim();
    const scopeId = this.scopeIdControl.value.trim();
    const clientSecret = this.form.controls['clientSecret'].value.trim();
    const clientSecretControl = this.form.controls['clientSecret'];
    const resolvedScope =
      this.selectedScope ?? this.findScopeByNameOrId(scopeId) ?? null;

    if (!this.isEditMode && !clientSecret) {
      clientSecretControl.setErrors({ required: true });
      clientSecretControl.markAsTouched();
      return;
    }

    const payload: ComponentCredentialDto = {
      component: componentId ? { id: componentId } : undefined,
      description: this.form.controls['description'].value.trim() || undefined,
      scope: resolvedScope
        ? {
            id: resolvedScope.id.trim(),
            name: resolvedScope.name.trim(),
          }
        : undefined,
      clientInfo: {
        clientId: this.form.controls['clientId'].value.trim() || undefined,
        clientSecret: clientSecret || undefined,
      },
    };

    this.saving = true;
    this.showResponseBanner = false;
    this.shouldNavigateAfterValidationDialogClose = false;
    const validationPayload: ComponentCredentialDto = this.isEditMode
      ? { ...payload, id: this.editingCredentialId! }
      : payload;

    this.componentCredentialsService
      .validateCredential(validationPayload)
      .pipe(take(1))
      .subscribe({
        next: (validationResponse) => {
          if (validationResponse.ok === false) {
            this.showValidationWarningDialog();
          }
          this.saveCredential(payload);
        },
        error: () => {
          this.saveCredential(payload);
        },
      });
  }

  cancel(): void {
    this.router.navigate(['/admin/licenses/component-credentials']);
  }

  private loadCredential(credentialId: string): void {
    this.loading = true;
    this.componentCredentialsService.getById(credentialId).subscribe({
      next: (credential) => {
        const loadedComponentId = credential.component?.id ?? '';
        if (loadedComponentId) {
          this.ensureComponentOption(
            loadedComponentId,
            credential.component?.componentName,
            credential.component?.description,
          );
        }
        this.selectedComponent =
          this.findComponentById(loadedComponentId) ??
          (loadedComponentId
            ? {
                id: loadedComponentId,
                componentName:
                  credential.component?.componentName ?? loadedComponentId,
                description: credential.component?.description ?? '',
              }
            : null);

        const loadedScopeId = credential.scope?.id?.trim() ?? '';
        const loadedScopeName = credential.scope?.name?.trim() ?? '';
        this.selectedScope =
          this.findScopeById(loadedScopeId) ??
          (loadedScopeId && loadedScopeName
            ? { id: loadedScopeId, name: loadedScopeName }
            : null);

        if (this.selectedScope) {
          this.ensureScopeOption(this.selectedScope);
        }

        this.form.patchValue({
          componentId: this.selectedComponent?.id ?? loadedComponentId,
          description: credential.description ?? '',
          scopeId: this.selectedScope?.id ?? loadedScopeId,
          clientId: credential.clientInfo?.clientId ?? '',
          clientSecret: '',
        });
        this.loading = false;
      },
      error: (error: unknown) => {
        this.errorMessage = this.resolveErrorMessage(
          error,
          'Failed to load component credential.',
        );
        this.successfulSave = false;
        this.showResponseBanner = true;
        this.loading = false;
      },
    });
  }

  private handleSaveSuccess(message: string): void {
    this.successfulSave = true;
    this.successMessage = message;
    this.showResponseBanner = true;
    this.saving = false;

    if (this.validationWarningDialogOpen) {
      this.shouldNavigateAfterValidationDialogClose = true;
      return;
    }

    this.navigateToCredentialsList();
  }

  private handleSaveError(error: unknown, fallbackMessage: string): void {
    this.successfulSave = false;
    this.errorMessage = this.resolveErrorMessage(error, fallbackMessage);
    this.showResponseBanner = true;
    this.saving = false;
  }

  private createCredential(payload: ComponentCredentialDto): void {
    this.componentCredentialsService.create(payload).subscribe({
      next: () => {
        this.handleSaveSuccess('Component credential created.');
      },
      error: (error: unknown) => {
        this.handleSaveError(error, 'Failed to create component credential.');
      },
    });
  }

  private updateCredential(payload: ComponentCredentialDto): void {
    const updatePayload: ComponentCredentialDto = {
      ...payload,
      id: this.editingCredentialId!,
    };

    this.componentCredentialsService
      .update(this.editingCredentialId!, updatePayload)
      .subscribe({
        next: () => {
          this.handleSaveSuccess('Component credential updated.');
        },
        error: (error: unknown) => {
          this.handleSaveError(error, 'Failed to update component credential.');
        },
      });
  }

  private saveCredential(payload: ComponentCredentialDto): void {
    if (this.isEditMode) {
      this.updateCredential(payload);
      return;
    }

    this.createCredential(payload);
  }

  private showValidationWarningDialog(): void {
    this.validationWarningDialogOpen = true;
    const dialogRef = this.dialog.open(InformationModalComponent, {
      data: {
        headerText: 'Credential Validation Failed',
        bodyText:
          'This credential failed the validation check. It will be saved for now, but you need to edit it.',
        cancelable: false,
      },
    });

    dialogRef.closed.pipe(take(1)).subscribe(() => {
      this.validationWarningDialogOpen = false;
      if (this.shouldNavigateAfterValidationDialogClose) {
        this.navigateToCredentialsList();
      }
    });
  }

  private navigateToCredentialsList(): void {
    setTimeout(() => {
      this.router.navigate(['/admin/licenses/component-credentials']);
    }, 600);
  }

  private resolveErrorMessage(error: unknown, fallbackMessage: string): string {
    if (!error) {
      return fallbackMessage;
    }
    if (typeof error === 'string') {
      return error;
    }
    if (error instanceof HttpErrorResponse) {
      if (typeof error.error === 'string' && error.error.trim()) {
        return error.error.trim();
      }
      if (
        typeof error.error === 'object' &&
        error.error &&
        typeof (error.error as { message?: unknown }).message === 'string'
      ) {
        return (error.error as { message: string }).message;
      }
      return fallbackMessage;
    }
    if (typeof error === 'object' && error !== null) {
      const typedError = error as {
        error?: { message?: string; title?: string };
        message?: string;
      };
      if (typedError.error?.message) {
        return typedError.error.message;
      }
      if (typedError.error?.title) {
        return typedError.error.title;
      }
      if (typedError.message) {
        return typedError.message;
      }
    }
    return fallbackMessage;
  }

  private loadScopes(): void {
    if (!this.licenseBatchStore.hasScopes()) {
      this.licenseBatchStore.getScopes();
      return;
    }

    this.scopes = [...this.licenseBatchStore.scopes()];
  }

  private loadComponents(): void {
    this.licensedProductService
      .getComponentsForLicensedProduct(true)
      .subscribe({
        next: (response) => {
          this.componentOptions = (response.list ?? []).filter(
            (component): component is LicensedProductComponentDto =>
              !!component?.id?.trim(),
          );

          const selectedComponentId =
            this.form.controls['componentId'].value.trim();
          if (selectedComponentId) {
            this.ensureComponentOption(selectedComponentId);
            this.selectedComponent =
              this.findComponentById(selectedComponentId) ??
              this.selectedComponent;
          }
        },
        error: () => {
          this.componentOptions = [];
          this.selectedComponent = null;
        },
      });
  }

  private ensureScopeOption(scope: ScopeDto): void {
    if (this.scopes.some((existingScope) => existingScope.id === scope.id)) {
      return;
    }

    this.scopes = [...this.scopes, scope];
  }

  private findScopeById(scopeId?: string): ScopeDto | undefined {
    const normalized = scopeId?.trim().toLowerCase();
    if (!normalized) {
      return undefined;
    }

    return this.scopes.find((scope) => scope.id.toLowerCase() === normalized);
  }

  private findScopeByNameOrId(value?: string): ScopeDto | undefined {
    const raw = value?.trim();
    const normalized = raw?.toLowerCase();
    if (!raw || !normalized) {
      return undefined;
    }

    const parsedId = this.extractIdFromFormattedValue(raw)?.toLowerCase();
    return this.scopes.find((scope) => {
      const scopeId = scope.id?.toLowerCase();
      const scopeName = scope.name?.toLowerCase();
      const formatted = this.formatScopeLabel(scope).toLowerCase();
      return (
        scopeId === normalized ||
        scopeName === normalized ||
        formatted === normalized ||
        (!!parsedId && scopeId === parsedId)
      );
    });
  }

  private extractIdFromFormattedValue(value: string): string | undefined {
    const match = value.match(/\(([^()]+)\)\s*$/);
    return match?.[1]?.trim();
  }

  private humanizeLabel(value: string): string {
    const trimmed = value?.trim() ?? '';
    if (!trimmed) {
      return '';
    }

    return trimmed
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  private findComponentById(
    componentId?: string,
  ): LicensedProductComponentDto | undefined {
    const normalized = componentId?.trim().toLowerCase();
    if (!normalized) {
      return undefined;
    }

    return this.componentOptions.find(
      (component) => component.id.toLowerCase() === normalized,
    );
  }

  private findComponentByNameOrId(
    value?: string,
  ): LicensedProductComponentDto | undefined {
    const raw = value?.trim();
    const normalized = raw?.toLowerCase();
    if (!raw || !normalized) {
      return undefined;
    }

    const parsedId = this.extractIdFromFormattedValue(raw)?.toLowerCase();
    return this.componentOptions.find((component) => {
      const componentId = component.id?.toLowerCase();
      const componentName = component.componentName?.toLowerCase();
      const formatted = this.componentFormatter(
        component,
        component.id,
      ).toLowerCase();
      return (
        componentId === normalized ||
        componentName === normalized ||
        formatted === normalized ||
        (!!parsedId && componentId === parsedId)
      );
    });
  }

  private ensureComponentOption(
    componentId: string,
    componentName?: string,
    description?: string,
  ): void {
    const normalizedId = componentId.trim();
    if (!normalizedId) {
      return;
    }

    if (
      this.componentOptions.some((component) => component.id === normalizedId)
    ) {
      return;
    }

    this.componentOptions = [
      ...this.componentOptions,
      {
        id: normalizedId,
        componentName: componentName?.trim() || normalizedId,
        description: description?.trim() || '',
      },
    ];
  }
}
